import type { SingleTradeInItem } from '../types/tradeInTypes';
import type { FuelEconomyApiVehicle } from '../types/fuelEconomyTypes';
import { normalizeVin } from './vinUtils';

export const getCombinedFuelEconomyByVin = (
    vehicles: FuelEconomyApiVehicle[] | undefined,
    vin: string | null | undefined
): number | undefined => {
    const normalizedVin = normalizeVin(vin);
    if (!Array.isArray(vehicles) || !normalizedVin) return undefined;

    return vehicles.find(
        (vehicle) => vehicle?.ok && normalizeVin(vehicle.vin) === normalizedVin && vehicle.fuelEconomy?.combined != null
    )?.fuelEconomy?.combined;
};

export const getCombinedFuelEconomyByVinMap = (vehicles: FuelEconomyApiVehicle[] | undefined): Map<string, number> => {
    const combinedFuelEconomyByVin = new Map<string, number>();
    if (!Array.isArray(vehicles)) return combinedFuelEconomyByVin;

    for (const vehicle of vehicles) {
        const combinedFuelEconomy = vehicle?.ok ? vehicle.fuelEconomy?.combined : undefined;
        const normalizedVin = normalizeVin(vehicle?.vin);
        if (normalizedVin && combinedFuelEconomy != null) {
            combinedFuelEconomyByVin.set(normalizedVin, combinedFuelEconomy);
        }
    }

    return combinedFuelEconomyByVin;
};

/**
 * Merges combined fuel economy values from the API into a list of trade-in items.
 * Returns the original array reference when no items were updated.
 */
export const mergeFuelEconomyIntoTradeInItems = (
    items: SingleTradeInItem[],
    combinedFuelEconomyByVin: Map<string, number>
): SingleTradeInItem[] => {
    type Acc = { items: SingleTradeInItem[]; changed: boolean };
    const result = items.reduce<Acc>(
        (acc, item) => {
            const normalizedVin = normalizeVin(item.tradeIn?.vin);
            const combinedFuelEconomy = normalizedVin ? combinedFuelEconomyByVin.get(normalizedVin) : undefined;
            if (combinedFuelEconomy == null) {
                acc.items.push(item);
                return acc;
            }
            acc.items.push({
                ...item,
                tradeIn: { ...item.tradeIn, fuelEconomy: combinedFuelEconomy, combinedFuelEconomy }
            });
            acc.changed = true;
            return acc;
        },
        { items: [], changed: false }
    );
    return result.changed ? result.items : items;
};
